import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { GridApi } from 'ag-grid-community';
import { APP_PERMISSIONS } from 'src/app/app-permissions';
import { APP_ROLE_OPTIONS, AppRoleOption, getRoleLabelsFromMask, maskIncludesRole } from 'src/app/app-roles';
import { AuthService } from 'src/app/auth.service';
import { MenuService } from 'src/app/menu.service';
import { MenuStateService } from 'src/app/menu-state.service';
import { DEFAULT_ROUTE_OPTIONS, DefaultRouteOption } from 'src/app/default-route-options';
import { CustomRendererComponent } from 'src/app/trip-lists/custom-renderer/custom-renderer.component';
import { UserModalComponent } from '../user-modal/user-modal.component';

interface MenuAccessSnapshot {
  menus: string[];
  subMenus: string[];
  subSubMenus: string[];
}

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss']
})
export class UserDetailsComponent implements OnInit {

  private readonly userDetailsUrl = '/api/users/user_details.php';
  private domainGridApi?: GridApi;
  private permissionGridApi?: GridApi;
  private currentUserId: number | null = null;
  private savedMenuAccess: MenuAccessSnapshot = {
    menus: [],
    subMenus: [],
    subSubMenus: [],
  };

  domains: any[] = [];
  permissions: any[] = [];
  ams_user: any = {};
  api_user: any = {};

  sideMenu: any[] = [];
  sideSubMenu: any[] = [];
  sideSubSubMenu: any[] = [];
  ams_side_menu_array: string[] = [];
  ams_side_sub_menu_array: string[] = [];
  ams_side_sub_sub_menu_array: string[] = [];

  canManageUsers = false;
  newPassword = '';
  confirmPassword = '';
  passwordSaving = false;
  passwordError = '';
  passwordSuccess = '';

  roleOptions: AppRoleOption[] = APP_ROLE_OPTIONS;
  selectedRoleKeys: string[] = [];
  roleSaving = false;
  roleError = '';
  roleSuccess = '';

  defaultRouteOptions: DefaultRouteOption[] = DEFAULT_ROUTE_OPTIONS;
  selectedDefaultRoute = '/dashboard';
  savedDefaultRoute = '/dashboard';
  defaultRouteSaving = false;
  defaultRouteError = '';
  defaultRouteSuccess = '';

  menuAccessSaving = false;
  menuAccessError = '';
  menuAccessSuccess = '';
  themeMode: 'light' | 'dark' = 'light';

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private dialog: MatDialog,
    private authService: AuthService,
    private menuService: MenuService,
    private menuStateService: MenuStateService
  ) { }

  ngOnInit(): void {
    this.menuStateService.themeState$.subscribe((theme) => {
      this.themeMode = theme === 'ams-content-dark' ? 'dark' : 'light';
    });

    this.authService.ensureCurrentUserLoaded().subscribe((currentUser) => {
      this.currentUserId = currentUser?.user_id ?? null;
      this.canManageUsers = !!currentUser && currentUser.permissions.includes(APP_PERMISSIONS.USERS_MANAGE);
    });

    this.route.paramMap.subscribe((params) => {
      const userId = params.get('uuid');

      if (!userId) {
        return;
      }

      this.loadUserDetails(userId);
      this.loadDomains(userId);
      this.loadUserPermissions(userId);
      this.loadMenuLists();
    });
  }

  get persistedRolesMask(): number {
    return this.parseRolesMask(this.ams_user?.roles_mask);
  }

  get persistedRoleSummary(): string {
    const roleLabels = getRoleLabelsFromMask(this.persistedRolesMask);
    return roleLabels.length > 0 ? roleLabels.join(', ') : 'None';
  }

  get selectedRolesMask(): number {
    return this.selectedRoleKeys.reduce((rolesMask, roleKey) => {
      const role = this.roleOptions.find((option) => option.key === roleKey);
      return role ? (rolesMask + role.value) : rolesMask;
    }, 0);
  }

  get selectedRoleSummary(): string {
    const roleLabels = this.roleOptions
      .filter((role) => this.selectedRoleKeys.includes(role.key))
      .map((role) => role.label);

    return roleLabels.length > 0 ? roleLabels.join(', ') : 'None';
  }

  get menuAccessHasChanges(): boolean {
    return !this.sameMenuIds(this.ams_side_menu_array, this.savedMenuAccess.menus)
      || !this.sameMenuIds(this.ams_side_sub_menu_array, this.savedMenuAccess.subMenus)
      || !this.sameMenuIds(this.ams_side_sub_sub_menu_array, this.savedMenuAccess.subSubMenus);
  }

  get defaultRouteHasChanges(): boolean {
    return this.selectedDefaultRoute !== this.savedDefaultRoute;
  }

  toggleTheme() {
    this.menuStateService.toggleTheme();
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  private loadUserDetails(userId: string) {
    this.http.get<any[]>(this.userDetailsUrl, { params: { table: 'users', id: userId } }).subscribe((response) => {
      if (!response?.length) {
        this.ams_user = {};
        this.api_user = {};
        this.resetDefaultRouteState(null);
        this.resetMenuAccess();
        return;
      }

      const user = response[0];
      this.ams_user = user;
      this.ams_side_menu_array = this.parseMenuArray(user?.ams_side_menu_array);
      this.ams_side_sub_menu_array = this.parseMenuArray(user?.ams_side_sub_menu_array);
      this.ams_side_sub_sub_menu_array = this.parseMenuArray(user?.ams_side_sub_sub_menu_array);
      this.captureMenuAccessSnapshot();
      this.syncSelectedRolesFromMask(this.parseRolesMask(user?.roles_mask));
      this.resetDefaultRouteState(user?.default_route);
      this.loadApiUser(user?.api_username, user?.domain_uuid);
    });
  }

  private loadApiUser(apiUsername: string, domainUuid: string) {
    if (!apiUsername || !domainUuid) {
      this.api_user = {};
      return;
    }

    this.http.get<any[]>(this.userDetailsUrl, {
      params: { table: 'ams_admin_user_manager', api_username: apiUsername, domain_uuid: domainUuid }
    }).subscribe((response) => {
      this.api_user = response?.length ? response[0] : {};
    });
  }

  private loadDomains(userId: string) {
    this.http.get<any[]>(this.userDetailsUrl, { params: { table: 'multi_domains', id: userId } }).subscribe((response) => {
      this.domains = (response ?? []).map((domain) => ({
        ...domain,
        domain_groups: '-'
      }));
      this.domains.forEach((domain) => this.loadDomainGroups(domain));
    });
  }

  private loadDomainGroups(domain: any) {
    if (!domain?.api_key || !domain?.domain_name) {
      domain.domain_groups = '-';
      return;
    }

    this.http.get<any>(this.userDetailsUrl, {
      params: { table: 'api_key_array', api_key: domain.api_key, domain_name: domain.domain_name }
    }).subscribe({
      next: (response) => {
        domain.domain_groups = this.formatDomainGroups(response);
        this.domainGridApi?.refreshCells({ force: true });
      },
      error: () => {
        domain.domain_groups = 'Unable to load';
        this.domainGridApi?.refreshCells({ force: true });
      }
    });
  }

  private formatDomainGroups(response: any): string {
    const groups = response?.[0]?.group;

    if (!Array.isArray(groups) || groups.length === 0) {
      return '-';
    }

    const groupNames = groups
      .map((group) => group?.group_name)
      .filter((groupName) => !!groupName);

    return groupNames.length ? groupNames.join(', ') : '-';
  }

  private loadUserPermissions(userId: string) {
    this.http.get<any>('/api/permission_manager/permission_manager.php', {
      params: { type: 'user_effective', user_id: userId }
    }).subscribe((response) => {
      this.permissions = response?.permissions ?? [];
      this.permissionGridApi?.refreshCells({ force: true });
    });
  }

  private loadMenuLists() {
    this.http.get<any[]>(this.userDetailsUrl, { params: { table: 'ams_side_menu' } }).subscribe((response) => {
      this.sideMenu = response ?? [];
      this.refreshDefaultRouteOptions(true);
    });
    this.http.get<any[]>(this.userDetailsUrl, { params: { table: 'ams_side_sub_menu' } }).subscribe((response) => {
      this.sideSubMenu = response ?? [];
      this.refreshDefaultRouteOptions(true);
    });
    this.http.get<any[]>(this.userDetailsUrl, { params: { table: 'ams_side_sub_sub_menu' } }).subscribe((response) => {
      this.sideSubSubMenu = response ?? [];
      this.refreshDefaultRouteOptions(true);
    });
  }

  private parseMenuArray(menuArray: string | null | undefined): string[] {
    if (!menuArray) {
      return [];
    }

    return this.normalizeMenuIds(menuArray.split(','));
  }

  private normalizeMenuIds(menuIds: any[]): string[] {
    const seen = new Set<string>();
    const normalizedIds: string[] = [];

    menuIds.forEach((menuId) => {
      const normalizedId = String(menuId ?? '').trim();

      if (normalizedId && !seen.has(normalizedId)) {
        seen.add(normalizedId);
        normalizedIds.push(normalizedId);
      }
    });

    return normalizedIds;
  }

  private parseRolesMask(value: any): number {
    const parsedMask = Number(value);
    return Number.isFinite(parsedMask) ? parsedMask : 0;
  }

  private syncSelectedRolesFromMask(rolesMask: number) {
    this.selectedRoleKeys = this.roleOptions
      .filter((role) => maskIncludesRole(rolesMask, role.value))
      .map((role) => role.key);
  }

  private resetDefaultRouteState(defaultRoute: unknown) {
    const normalizedRoute = this.authService.normalizeDefaultRoute(defaultRoute) || this.authService.fallbackDefaultRoute;
    this.selectedDefaultRoute = normalizedRoute;
    this.savedDefaultRoute = normalizedRoute;
    this.refreshDefaultRouteOptions(false);
    this.defaultRouteError = '';
    this.defaultRouteSuccess = '';
  }

  private refreshDefaultRouteOptions(repairInaccessibleDefault = false) {
    this.defaultRouteOptions = this.buildDefaultRouteOptions();

    if (repairInaccessibleDefault) {
      this.ensureSelectedDefaultRouteIsAccessible();
    }
  }

  private buildDefaultRouteOptions(): DefaultRouteOption[] {
    const options: DefaultRouteOption[] = [];

    DEFAULT_ROUTE_OPTIONS.forEach((option) => this.addDefaultRouteOption(options, option));
    this.buildMenuRouteOptions().forEach((option) => this.addDefaultRouteOption(options, option));

    return options;
  }

  private buildMenuRouteOptions(): DefaultRouteOption[] {
    const routeOptions: DefaultRouteOption[] = [];
    const selectedSubMenuIds = new Set(this.ams_side_sub_menu_array);
    const selectedSubSubMenuIds = new Set(this.ams_side_sub_sub_menu_array);

    this.sideSubMenu.forEach((subMenu) => {
      if (!selectedSubMenuIds.has(String(subMenu?.ams_side_sub_menu_id ?? '').trim())) {
        return;
      }

      const routeOption = this.buildMenuRouteOption(subMenu?.ams_side_sub_menu_page, subMenu?.ams_side_sub_menu_text);

      if (routeOption) {
        routeOptions.push(routeOption);
      }
    });

    this.sideSubSubMenu.forEach((subSubMenu) => {
      if (!selectedSubSubMenuIds.has(String(subSubMenu?.ams_side_sub_sub_menu_id ?? '').trim())) {
        return;
      }

      const routeOption = this.buildMenuRouteOption(subSubMenu?.ams_side_sub_sub_menu_page, subSubMenu?.ams_side_sub_sub_menu_text);

      if (routeOption) {
        routeOptions.push(routeOption);
      }
    });

    return routeOptions.sort((first, second) => first.label.localeCompare(second.label) || first.route.localeCompare(second.route));
  }

  private buildMenuRouteOption(routeValue: unknown, labelValue: unknown): DefaultRouteOption | null {
    const route = this.normalizeRouteOption(routeValue);

    if (!route) {
      return null;
    }

    return {
      label: this.routeOptionLabel(labelValue, route),
      route,
    };
  }

  private addDefaultRouteOption(options: DefaultRouteOption[], option: DefaultRouteOption) {
    if (!option.route || options.some((existingOption) => existingOption.route === option.route)) {
      return;
    }

    options.push(option);
  }

  private ensureSelectedDefaultRouteIsAccessible() {
    if (!this.selectedDefaultRoute || this.defaultRouteOptions.length === 0) {
      return;
    }

    if (this.defaultRouteOptions.some((option) => option.route === this.selectedDefaultRoute)) {
      if (this.defaultRouteError === 'Current default page is not selected in Menu Access. Pick and save a new default page.') {
        this.defaultRouteError = '';
      }
      return;
    }

    const replacementRoute = this.defaultRouteOptions[0]?.route;

    if (replacementRoute) {
      this.selectedDefaultRoute = replacementRoute;
    }

    this.defaultRouteError = 'Current default page is not selected in Menu Access. Pick and save a new default page.';
  }

  private savedDefaultRouteIsAccessible(): boolean {
    return !this.savedDefaultRoute || this.defaultRouteOptions.some((option) => option.route === this.savedDefaultRoute);
  }

  private handleMenuAccessChanged() {
    this.refreshDefaultRouteOptions(true);
  }

  private normalizeRouteOption(routeValue: unknown): string {
    const rawRoute = String(routeValue ?? '').trim();

    if (!rawRoute || rawRoute === '#' || ['null', 'undefined'].includes(rawRoute.toLowerCase())) {
      return '';
    }

    const normalizedRoute = this.authService.normalizeDefaultRoute(rawRoute);

    return /^\/[A-Za-z0-9_\/-]*$/.test(normalizedRoute) ? normalizedRoute : '';
  }

  private routeOptionLabel(labelValue: unknown, route: string): string {
    const label = String(labelValue ?? '').trim();
    return label || this.formatDefaultRouteLabel(route);
  }

  private formatDefaultRouteLabel(route: string): string {
    const routeText = route.replace(/^\/+/, '').replace(/[-_]+/g, ' ').trim();

    if (!routeText) {
      return route;
    }

    return routeText.replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  domainColumnDefs = [
    { field: 'domain_name', headerName: 'Domain Name', minWidth: 220, flex: 1 },
    { field: 'multi_domain_uuid', headerName: 'Domain UUID', minWidth: 260, flex: 1 },
    { field: 'domain_groups', headerName: 'Groups', minWidth: 320, flex: 1.4 },
    { field: 'api_username', headerName: 'Username', minWidth: 180, flex: 0.8 },
    { field: 'api_key', headerName: 'API Key', minWidth: 180, flex: 0.8 },
  ];

  permissionColumnDefs = [
    { field: 'group_key', headerName: 'Group', minWidth: 160, flex: 0.65 },
    { field: 'label', headerName: 'Target', minWidth: 230, flex: 1 },
    { field: 'permission_key', headerName: 'Permission Key', minWidth: 260, flex: 1.1 },
    { field: 'target_type', headerName: 'Type', width: 120 },
    { field: 'source', headerName: 'Source', minWidth: 150, flex: 0.6 },
    { headerName: 'Create', width: 110, valueGetter: (params: any) => params.data?.effective?.create ? 'Yes' : '-' },
    { headerName: 'Read', width: 110, valueGetter: (params: any) => params.data?.effective?.read ? 'Yes' : '-' },
    { headerName: 'Update', width: 110, valueGetter: (params: any) => params.data?.effective?.update ? 'Yes' : '-' },
    { headerName: 'Delete', width: 110, valueGetter: (params: any) => params.data?.effective?.delete ? 'Yes' : '-' },
    { field: 'description', headerName: 'Description', minWidth: 360, flex: 1.4 },
  ];

  defaultColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
    },
    autoHeight: true,
    cellClass: 'cell-wrap-text',
    resizable: true,
    editable: false,
  };

  onDomainGridReady(params: any) {
    this.domainGridApi = params.api;
    this.autoSizeGrid(this.domainGridApi, false);
  }

  onPermissionGridReady(params: any) {
    this.permissionGridApi = params.api;
    this.autoSizeGrid(this.permissionGridApi, false);
  }

  private autoSizeGrid(gridApi: GridApi | undefined, skipHeader: boolean) {
    const columns = gridApi?.getColumns();

    if (!columns?.length) {
      return;
    }

    gridApi?.autoSizeColumns(columns.map((column) => column.getId()), skipHeader);
  }

  isSideMenuChecked(id: number): boolean {
    return this.ams_side_menu_array.includes(String(id));
  }

  isSideSubMenuChecked(id: number): boolean {
    return this.ams_side_sub_menu_array.includes(String(id));
  }

  isSideSubSubMenuChecked(id: number): boolean {
    return this.ams_side_sub_sub_menu_array.includes(String(id));
  }

  getSideSubMenus(menuId: number): any[] {
    return this.sideSubMenu.filter((subMenu) => String(subMenu.ams_side_menu_id) === String(menuId));
  }

  getSideSubSubMenus(subMenuId: number): any[] {
    return this.sideSubSubMenu.filter((subSubMenu) => String(subSubMenu.ams_side_sub_menu_id) === String(subMenuId));
  }

  editAmsUser() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '700px';
    dialogConfig.height = '800px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Ams User',
      },
    };
    this.dialog.open(UserModalComponent, dialogConfig);
  }

  editApiUser() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '700px';
    dialogConfig.height = '800px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Api User',
      },
    };
    this.dialog.open(UserModalComponent, dialogConfig);
  }

  editPhoneSystem() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '700px';
    dialogConfig.height = '800px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Phone System',
        phoneObj: this.ams_user,
      },
    };
    this.dialog.open(UserModalComponent, dialogConfig);
  }

  addDomainAccess() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '700px';
    dialogConfig.height = '800px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Add Domain Access',
      },
    };
    this.dialog.open(UserModalComponent, dialogConfig);
  }

  toggleSideMenu(menuId: any, checked: boolean) {
    this.clearMenuAccessMessages();
    this.ams_side_menu_array = this.setMenuIdChecked(this.ams_side_menu_array, menuId, checked);

    if (!checked) {
      const childSubMenuIds = this.getSideSubMenus(Number(menuId)).map((subMenu) => String(subMenu.ams_side_sub_menu_id));
      const childSubMenuIdSet = new Set(childSubMenuIds);
      const childSubSubMenuIds = this.sideSubSubMenu
        .filter((subSubMenu) => childSubMenuIdSet.has(String(subSubMenu.ams_side_sub_menu_id)))
        .map((subSubMenu) => String(subSubMenu.ams_side_sub_sub_menu_id));
      const childSubSubMenuIdSet = new Set(childSubSubMenuIds);

      this.ams_side_sub_menu_array = this.ams_side_sub_menu_array.filter((id) => !childSubMenuIdSet.has(id));
      this.ams_side_sub_sub_menu_array = this.ams_side_sub_sub_menu_array.filter((id) => !childSubSubMenuIdSet.has(id));
    }

    this.handleMenuAccessChanged();
  }

  toggleSideSubMenu(subMenuId: any, parentMenuId: any, checked: boolean) {
    this.clearMenuAccessMessages();
    this.ams_side_sub_menu_array = this.setMenuIdChecked(this.ams_side_sub_menu_array, subMenuId, checked);

    if (checked) {
      this.ams_side_menu_array = this.setMenuIdChecked(this.ams_side_menu_array, parentMenuId, true);
      this.handleMenuAccessChanged();
      return;
    }

    const childSubSubMenuIds = this.getSideSubSubMenus(Number(subMenuId))
      .map((subSubMenu) => String(subSubMenu.ams_side_sub_sub_menu_id));
    const childSubSubMenuIdSet = new Set(childSubSubMenuIds);
    this.ams_side_sub_sub_menu_array = this.ams_side_sub_sub_menu_array.filter((id) => !childSubSubMenuIdSet.has(id));
    this.handleMenuAccessChanged();
  }

  toggleSideSubSubMenu(subSubMenuId: any, parentSubMenuId: any, checked: boolean) {
    this.clearMenuAccessMessages();
    this.ams_side_sub_sub_menu_array = this.setMenuIdChecked(this.ams_side_sub_sub_menu_array, subSubMenuId, checked);

    if (!checked) {
      this.handleMenuAccessChanged();
      return;
    }

    const parentSubMenu = this.sideSubMenu.find((subMenu) => String(subMenu.ams_side_sub_menu_id) === String(parentSubMenuId));
    this.ams_side_sub_menu_array = this.setMenuIdChecked(this.ams_side_sub_menu_array, parentSubMenuId, true);

    if (parentSubMenu?.ams_side_menu_id) {
      this.ams_side_menu_array = this.setMenuIdChecked(this.ams_side_menu_array, parentSubMenu.ams_side_menu_id, true);
    }

    this.handleMenuAccessChanged();
  }

  updateMenuAccess() {
    this.clearMenuAccessMessages();

    if (!this.canManageUsers) {
      this.menuAccessError = 'You do not have permission to change menu access.';
      return;
    }

    if (!this.ams_user?.id) {
      this.menuAccessError = 'User details are not loaded yet.';
      return;
    }

    this.refreshDefaultRouteOptions(true);

    if (!this.savedDefaultRouteIsAccessible()) {
      this.menuAccessError = 'Save a default page that is still selected in Menu Access before saving menu access.';
      this.defaultRouteError = 'Current saved default page is not selected in Menu Access. Pick and save a new default page first.';
      return;
    }

    this.menuAccessSaving = true;

    const postData = {
      ams_side_menu_array: this.serializeSelectedIds(this.ams_side_menu_array, this.sideMenu, 'ams_side_menu_id'),
      ams_side_sub_menu_array: this.serializeSelectedIds(this.ams_side_sub_menu_array, this.sideSubMenu, 'ams_side_sub_menu_id'),
      ams_side_sub_sub_menu_array: this.serializeSelectedIds(this.ams_side_sub_sub_menu_array, this.sideSubSubMenu, 'ams_side_sub_sub_menu_id'),
      user_id: this.ams_user.id
    };

    this.http.post<any>('/api/users/user_menu_access.php', postData).subscribe({
      next: (response) => {
        this.menuAccessSaving = false;
        this.menuAccessSuccess = response?.message === 'ok' ? 'Menu access updated.' : (response?.message || 'Menu access updated.');
        this.ams_user = {
          ...this.ams_user,
          ams_side_menu_array: response?.ams_side_menu_array ?? postData.ams_side_menu_array,
          ams_side_sub_menu_array: response?.ams_side_sub_menu_array ?? postData.ams_side_sub_menu_array,
          ams_side_sub_sub_menu_array: response?.ams_side_sub_sub_menu_array ?? postData.ams_side_sub_sub_menu_array,
        };
        this.ams_side_menu_array = this.parseMenuArray(this.ams_user.ams_side_menu_array);
        this.ams_side_sub_menu_array = this.parseMenuArray(this.ams_user.ams_side_sub_menu_array);
        this.ams_side_sub_sub_menu_array = this.parseMenuArray(this.ams_user.ams_side_sub_sub_menu_array);
        this.captureMenuAccessSnapshot();
        this.refreshDefaultRouteOptions();
        this.refreshCurrentUserMenuIfNeeded();
      },
      error: (error) => {
        this.menuAccessSaving = false;
        this.menuAccessError = error?.error?.message || 'Unable to update menu access.';
      }
    });
  }

  private setMenuIdChecked(menuIds: string[], menuId: any, checked: boolean): string[] {
    const normalizedId = String(menuId ?? '').trim();

    if (!normalizedId) {
      return menuIds;
    }

    const menuIdSet = new Set(menuIds);

    if (checked) {
      menuIdSet.add(normalizedId);
    } else {
      menuIdSet.delete(normalizedId);
    }

    return Array.from(menuIdSet);
  }

  private serializeSelectedIds(selectedIds: string[], orderedItems: any[], idKey: string): string | null {
    const selectedIdSet = new Set(this.normalizeMenuIds(selectedIds));
    const orderedSelectedIds = orderedItems
      .map((item) => String(item?.[idKey] ?? '').trim())
      .filter((id) => id && selectedIdSet.has(id));
    const extraSelectedIds = Array.from(selectedIdSet).filter((id) => !orderedSelectedIds.includes(id));
    const serializedIds = [...orderedSelectedIds, ...extraSelectedIds];

    return serializedIds.length ? serializedIds.join(', ') : null;
  }

  private captureMenuAccessSnapshot() {
    this.savedMenuAccess = {
      menus: [...this.ams_side_menu_array],
      subMenus: [...this.ams_side_sub_menu_array],
      subSubMenus: [...this.ams_side_sub_sub_menu_array],
    };
  }

  private resetMenuAccess() {
    this.ams_side_menu_array = [];
    this.ams_side_sub_menu_array = [];
    this.ams_side_sub_sub_menu_array = [];
    this.captureMenuAccessSnapshot();
  }

  private sameMenuIds(firstMenuIds: string[], secondMenuIds: string[]): boolean {
    const first = this.normalizeMenuIds(firstMenuIds).sort();
    const second = this.normalizeMenuIds(secondMenuIds).sort();

    return first.length === second.length && first.every((id, index) => id === second[index]);
  }

  private clearMenuAccessMessages() {
    this.menuAccessError = '';
    this.menuAccessSuccess = '';
  }

  private refreshCurrentUserMenuIfNeeded() {
    if (this.currentUserId !== null && Number(this.ams_user?.id) === this.currentUserId) {
      localStorage.removeItem('menuData');
      this.menuService.fetchMenuData().subscribe({
        error: () => undefined
      });
    }
  }

  private refreshCurrentUserIfNeeded() {
    if (this.currentUserId !== null && Number(this.ams_user?.id) === this.currentUserId) {
      this.authService.loadCurrentUser(true).subscribe({
        error: () => undefined
      });
    }
  }

  saveDefaultRoute() {
    this.defaultRouteError = '';
    this.defaultRouteSuccess = '';

    if (!this.canManageUsers) {
      this.defaultRouteError = 'You do not have permission to change default pages.';
      return;
    }

    if (!this.ams_user?.id) {
      this.defaultRouteError = 'User details are not loaded yet.';
      return;
    }

    const normalizedRoute = this.authService.normalizeDefaultRoute(this.selectedDefaultRoute) || this.authService.fallbackDefaultRoute;
    this.selectedDefaultRoute = normalizedRoute;
    this.defaultRouteSaving = true;

    this.http.post<any>('/api/users/update_default_route.php', {
      user_id: this.ams_user.id,
      default_route: normalizedRoute === this.authService.fallbackDefaultRoute ? null : normalizedRoute
    }).subscribe({
      next: (response) => {
        const savedRoute = this.authService.normalizeDefaultRoute(response?.default_route) || this.authService.fallbackDefaultRoute;
        this.defaultRouteSaving = false;
        this.defaultRouteSuccess = response?.message || 'Default page updated.';
        this.refreshDefaultRouteOptions();
        this.selectedDefaultRoute = savedRoute;
        this.savedDefaultRoute = savedRoute;
        this.ams_user = {
          ...this.ams_user,
          default_route: response?.default_route ?? null,
        };
        this.refreshCurrentUserIfNeeded();
      },
      error: (error) => {
        this.defaultRouteSaving = false;
        this.defaultRouteError = error?.error?.message || 'Unable to update default page.';
      }
    });
  }

  changeUserPassword() {
    this.passwordError = '';
    this.passwordSuccess = '';

    if (!this.canManageUsers) {
      this.passwordError = 'You do not have permission to change user passwords.';
      return;
    }

    if (!this.ams_user?.id) {
      this.passwordError = 'User details are not loaded yet.';
      return;
    }

    if (!this.newPassword.trim()) {
      this.passwordError = 'Enter a new password.';
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.passwordError = 'Passwords do not match.';
      return;
    }

    this.passwordSaving = true;

    this.http.post<any>('/api/users/change_password.php', {
      user_id: this.ams_user.id,
      password: this.newPassword
    }).subscribe({
      next: (response) => {
        this.passwordSaving = false;
        this.passwordSuccess = response?.message || 'Password updated successfully.';
        this.newPassword = '';
        this.confirmPassword = '';
      },
      error: (error) => {
        this.passwordSaving = false;
        this.passwordError = error?.error?.message || 'Unable to change password.';
      }
    });
  }

  isRoleSelected(roleKey: string): boolean {
    return this.selectedRoleKeys.includes(roleKey);
  }

  toggleRole(roleKey: string, checked: boolean) {
    this.roleError = '';
    this.roleSuccess = '';

    if (checked) {
      this.selectedRoleKeys = Array.from(new Set([...this.selectedRoleKeys, roleKey]));
      return;
    }

    this.selectedRoleKeys = this.selectedRoleKeys.filter((selectedRoleKey) => selectedRoleKey !== roleKey);
  }

  saveRoles() {
    this.roleError = '';
    this.roleSuccess = '';

    if (!this.canManageUsers) {
      this.roleError = 'You do not have permission to change user roles.';
      return;
    }

    if (!this.ams_user?.id) {
      this.roleError = 'User details are not loaded yet.';
      return;
    }

    this.roleSaving = true;

    this.http.post<any>('/api/users/update_roles.php', {
      user_id: this.ams_user.id,
      roles: this.selectedRoleKeys,
      sync_permissions: true,
      sync_menu_access: true
    }).subscribe({
      next: (response) => {
        this.roleSaving = false;
        this.roleSuccess = response?.message || 'Roles updated successfully.';
        this.ams_user = {
          ...this.ams_user,
          roles_mask: response?.roles_mask ?? this.selectedRolesMask,
          ams_side_menu_array: response?.menu_access?.ams_side_menu_array ?? null,
          ams_side_sub_menu_array: response?.menu_access?.ams_side_sub_menu_array ?? null,
          ams_side_sub_sub_menu_array: response?.menu_access?.ams_side_sub_sub_menu_array ?? null,
        };
        this.ams_side_menu_array = this.parseMenuArray(response?.menu_access?.ams_side_menu_array);
        this.ams_side_sub_menu_array = this.parseMenuArray(response?.menu_access?.ams_side_sub_menu_array);
        this.ams_side_sub_sub_menu_array = this.parseMenuArray(response?.menu_access?.ams_side_sub_sub_menu_array);
        this.captureMenuAccessSnapshot();
        this.syncSelectedRolesFromMask(this.parseRolesMask(response?.roles_mask));
        this.loadUserPermissions(String(this.ams_user.id));
        this.refreshCurrentUserMenuIfNeeded();
      },
      error: (error) => {
        this.roleSaving = false;
        this.roleError = error?.error?.message || 'Unable to update roles.';
      }
    });
  }
}
